import React, { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLanguage } from "../../../contexts/LanguageContext";
import { getSiteSearchHits, hitMatchesQuery } from "../../../data/siteSearchIndex";

const SiteSearchResultsPage: React.FC = () => {
  const { t, isRTL } = useLanguage();
  const [searchParams] = useSearchParams();
  const rawQ = (searchParams.get("q") || "").trim();

  const hits = useMemo(() => getSiteSearchHits(t), [t]);

  const visible = useMemo(() => {
    if (!rawQ) return hits;
    return hits.filter((h) => hitMatchesQuery(h, rawQ));
  }, [hits, rawQ]);

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? "rtl" : "ltr"}>
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <header className={`mb-10 ${isRTL ? "text-right" : "text-left"}`}>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              {t("siteSearchResultsTitle")}
            </h1>
            {rawQ ? (
              <p className="text-lg text-gray-600">
                {t("siteSearchResultsHint")}{" "}
                <span className="font-semibold text-sa-700">«{rawQ}»</span>
              </p>
            ) : (
              <p className="text-lg text-gray-600">{t("siteSearchResultsHint")}</p>
            )}
          </header>

          <div className="flex justify-center">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl w-full">
              {visible.length === 0 && rawQ ? (
                <p className={`col-span-full text-center text-gray-600 py-12 ${isRTL ? "text-right" : "text-left"}`}>
                  {t("searchNoResults")}
                </p>
              ) : null}
              {visible.map((hit) => (
                <Link
                  key={hit.id}
                  to={hit.path}
                  className="group block h-full focus:outline-none focus-visible:ring-4 focus-visible:ring-sa-200 rounded-2xl"
                >
                  <article className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-transform duration-300 group-hover:-translate-y-1 flex flex-col h-full">
                    <div className="w-12 h-12 bg-sa-100 rounded-full flex items-center justify-center mb-4">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        className="text-sa-600"
                      >
                        <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <h2
                      className={`text-xl font-bold text-gray-900 mb-2 group-hover:text-sa-700 transition-colors ${
                        isRTL ? "text-right" : "text-left"
                      }`}
                    >
                      {hit.title}
                    </h2>
                    {hit.description ? (
                      <p
                        className={`text-sm text-gray-600 leading-relaxed mb-4 flex-grow line-clamp-3 ${
                          isRTL ? "text-right" : "text-left"
                        }`}
                      >
                        {hit.description}
                      </p>
                    ) : (
                      <div className="flex-grow" />
                    )}
                    <div className="flex items-center justify-start pt-4 border-t border-gray-100 mt-auto">
                      <span className="badge bg-success bg-opacity-10 text-success px-3 py-2 text-xs font-medium">
                        {hit.category}
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SiteSearchResultsPage;
